import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useFeatureFlag } from '@hooks/useFeatureFlag';
import { ModalContext } from '@organisms/Modals/ModalContext';
import DebugErrorModal from '@organisms/Modals/DebugErrorModal';
import { FeatureFlagType } from '@services/Schemas/FeatureFlagSchema';
import { DebugErrorInfo } from '@/types/DebugErrorInfo';

/**
 * Context type for debug error modal management
 */
export interface DebugErrorContextType {
  /** Whether debug error details feature is enabled */
  isDebugEnabled: boolean;
  /** Show the debug error modal */
  showDebugError: (statusCode: number, errorMessage: string, debugInfo: DebugErrorInfo) => void;
}

/**
 * Default context value when provider is not available.
 * Provides safe no-op implementations.
 */
const defaultContext: DebugErrorContextType = {
  isDebugEnabled: false,
  showDebugError: () => {
    /* no-op when provider not available */
  },
};

/**
 * Debug error context with safe default value
 */
const DebugErrorContext = createContext<DebugErrorContextType>(defaultContext);

/**
 * Hook to access debug error context.
 * Returns safe defaults if provider is not available.
 */
export function useDebugError() {
  return useContext(DebugErrorContext);
}

/**
 * Provider component for debug error context.
 * Must be placed inside ModalProvider.
 */
export function DebugErrorProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const { openModal } = useContext(ModalContext);
  const isDebugEnabled = useFeatureFlag(FeatureFlagType.DebugErrorDetails);

  const showDebugError = useCallback(
    (statusCode: number, errorMessage: string, debugInfo: DebugErrorInfo) => {
      if (!isDebugEnabled) return;

      openModal(
        <DebugErrorModal
          statusCode={statusCode}
          errorMessage={errorMessage}
          debugInfo={debugInfo}
        />
      );
    },
    [isDebugEnabled, openModal]
  );

  const value = useMemo<DebugErrorContextType>(
    () => ({
      isDebugEnabled,
      showDebugError,
    }),
    [isDebugEnabled, showDebugError]
  );

  return (
    <DebugErrorContext.Provider value={value}>{children}</DebugErrorContext.Provider>
  );
}

export default DebugErrorContext;
